import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Download, ExternalLink } from "lucide-react";
import { listAnalyses, type AnalysisRow } from "@/lib/admin.functions";
import { getDeckFileUrl } from "@/lib/deck.functions";
import { useRoleGuard } from "@/hooks/use-role-guard";

export const Route = createFileRoute("/admin/analyses")({
  head: () => ({
    meta: [
      { title: "Analyses - Admin - DeckScore" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AnalysesPage,
});

function AnalysesPage() {
  const { status: roleStatus } = useRoleGuard(["admin", "super_admin"]);
  const runList = useServerFn(listAnalyses);
  const runGetUrl = useServerFn(getDeckFileUrl);
  const [rows, setRows] = useState<AnalysisRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const { analyses } = await runList();
        setRows(analyses);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleDownload(deckId: string) {
    setDownloading(deckId);
    try {
      const { url } = await runGetUrl({ data: { deckId } });
      if (url) window.open(url, "_blank", "noopener,noreferrer");
      else alert("Deck file not found");
    } finally {
      setDownloading(null);
    }
  }

  if (roleStatus !== "ok") return null;
  return (
    <>
      <div className="mb-6">
        <p className="font-script text-accent text-[22px] leading-none">insights</p>
        <h1 className="font-display text-[28px] font-semibold mt-2">Analyses</h1>
      </div>

      <div className="bg-surface border border-line rounded-2xl overflow-hidden">
        <table className="w-full text-[14px]">
          <thead className="bg-surface-sunken text-ink-secondary">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Company</th>
              <th className="text-left px-4 py-3 font-medium">Score</th>
              <th className="text-left px-4 py-3 font-medium">Founder</th>
              <th className="text-left px-4 py-3 font-medium">Date</th>
              <th className="text-left px-4 py-3 font-medium">Order?</th>
              <th className="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-ink-tertiary">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-ink-tertiary">
                  No analyses yet.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.deck_id} className="border-t border-line">
                  <td className="px-4 py-3 font-medium">{r.company}</td>
                  <td className="px-4 py-3 text-ink-secondary">
                    {r.overall_score != null ? `${r.overall_score}/100` : "-"}
                  </td>
                  <td className="px-4 py-3 text-ink-secondary">{r.founder_email ?? "-"}</td>
                  <td className="px-4 py-3 text-ink-secondary">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString() : "-"}
                  </td>
                  <td className="px-4 py-3">
                    {r.has_order ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-accent-soft text-accent-ink">
                        Yes
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-surface-sunken text-ink-tertiary">
                        No
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Link
                      to="/results"
                      search={{ deck: r.deck_id }}
                      className="inline-flex items-center gap-1 text-[13px] text-ink-secondary hover:text-ink px-2 py-1"
                    >
                      <ExternalLink className="w-3.5 h-3.5" /> View
                    </Link>
                    <button
                      onClick={() => handleDownload(r.deck_id)}
                      disabled={downloading === r.deck_id}
                      className="inline-flex items-center gap-1 text-[13px] text-ink-secondary hover:text-ink px-2 py-1 ml-1 disabled:opacity-60"
                    >
                      <Download className="w-3.5 h-3.5" />
                      {downloading === r.deck_id ? "…" : "Download deck"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
